import { isEmpty, isNil, sum, times, uniqBy } from 'lodash';
import { UploadFilesErrorMessages } from 'shared/enums';
import { ensureBatchFilesNotExecutable } from 'shared/helpers';
import {
  IMAGE_UPLOAD_FAILED_MESSAGE,
  INITIAL_CONNECTION_TIMEOUT_MS,
  MAX_CONCURRENT_IMAGE_UPLOADS,
  NETWORK_PING_INTERVAL_MS,
  nonCancellableImageSetUploadStages,
  pausableImageSetUploadStages,
  PRESIGNED_URL_BATCH_SIZE,
  PROGRESS_CONNECTION_TIMEOUT_MS,
  timedImageSetUploadStages,
  VALIDATION_ERROR_PREFIX,
} from './constants';
import { ImageSetUploadStage, UploadErrorAction } from './enums';
import {
  completeImagesUpload,
  fetchPresignedUrls,
  fetchUploadedImageNames,
  invalidateIterationDataset,
  markImageUploaded,
  pingBackend,
  putImage,
} from './image-set-requests';
import type {
  ImageSetUploader,
  ImageSetUploaderOptions,
  ImageSetUploadSnapshot,
  ReadyImageUpload,
  RequestOutcome,
  UploadRun,
} from './types';
import {
  diffAgainstServerImages,
  getRetryDelay,
  getUploadErrorAction,
  getUploadErrorMessage,
  MissingPresignedUrlsError,
} from './utils';

const failedOutcome: RequestOutcome<never> = { isSuccessful: false };

// Resolves after the delay, or straight away once the signal aborts.
const wait = (durationMs: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, durationMs);

    signal.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Creates the upload engine for one set of iteration images.
 *
 * - Up to `MAX_CONCURRENT_IMAGE_UPLOADS` images are in flight at once.
 * - Presigned URLs are fetched `PRESIGNED_URL_BATCH_SIZE` at a time, one batch request at a time.
 * - Failed requests are retried as `getUploadErrorAction` decides.
 * - On network loss it pings the backend and resumes once the backend is reachable.
 * - Every start and resume first checks which images the server already has.
 *
 * Each start or resume is a "run". Stopping a run aborts its requests, and late results from
 * a stopped run are ignored.
 *
 * @param options - The files to upload, their iteration dataset and a listener for stage changes.
 * @returns Controls for the upload and a snapshot of its progress.
 */
export const createImageSetUploader = ({
  files: selectedFiles,
  iterationDatasetId,
  onChange,
}: ImageSetUploaderOptions): ImageSetUploader => {
  // The server identifies images by filename, so each name is uploaded once.
  const files = uniqBy(selectedFiles, 'name');
  const fileSizeByName = new Map(files.map(({ name, size }) => [name, size]));
  const totalBytes = sum(files.map(({ size }) => size));
  const completedImageNames = new Set<string>();
  const inFlightBytesByName = new Map<string, number>();

  let stage = ImageSetUploadStage.Queued;
  let error: string | undefined;
  let activeRun: UploadRun | null = null;
  let pendingFiles: File[] = [];
  let readyUploads: ReadyImageUpload[] = [];
  let presignedUrlsRequest: Promise<void> | null = null;
  let activePutCount = 0;
  let completedBytes = 0;
  let accumulatedActiveMs = 0;
  let activeSince: number | null = null;
  let watchdogTimer: ReturnType<typeof setTimeout> | undefined;
  let pingTimer: ReturnType<typeof setTimeout> | undefined;
  // Increases whenever a run stops, so a ping loop from an earlier network error ends.
  let pingGeneration = 0;
  let isDisposed = false;

  const isCurrentRun = (run: UploadRun) => run === activeRun;

  const getActiveElapsedMs = () =>
    isNil(activeSince)
      ? accumulatedActiveMs
      : accumulatedActiveMs + Date.now() - activeSince;

  const getSnapshot = (): ImageSetUploadSnapshot => ({
    stage,
    error,
    uploadedBytes: completedBytes + sum([...inFlightBytesByName.values()]),
    totalBytes,
    completedCount: completedImageNames.size,
    totalCount: files.length,
    activeElapsedMs: getActiveElapsedMs(),
  });

  const updateActiveTime = (nextStage: ImageSetUploadStage) => {
    const isTimed = timedImageSetUploadStages.includes(nextStage);

    if (isTimed && isNil(activeSince)) {
      activeSince = Date.now();
    }

    if (!isTimed && !isNil(activeSince)) {
      accumulatedActiveMs += Date.now() - activeSince;
      activeSince = null;
    }
  };

  const setStage = (nextStage: ImageSetUploadStage, nextError?: string) => {
    if (isDisposed) {
      return;
    }

    updateActiveTime(nextStage);
    stage = nextStage;
    error = nextError;
    onChange(getSnapshot());
  };

  const markImageCompleted = (name: string) => {
    if (completedImageNames.has(name)) {
      return;
    }

    completedImageNames.add(name);
    completedBytes += fileSizeByName.get(name) ?? 0;
  };

  const stopRun = () => {
    activeRun?.controller.abort();
    activeRun = null;
    clearTimeout(watchdogTimer);
    clearTimeout(pingTimer);
    pingGeneration += 1;
    activePutCount = 0;
    inFlightBytesByName.clear();
    readyUploads = [];
    presignedUrlsRequest = null;
  };

  const beginRun = (): UploadRun => {
    stopRun();
    activeRun = { controller: new AbortController() };
    return activeRun;
  };

  // Tells the backend the upload is over, keeping the images uploaded so far.
  const sendUploadDone = async (): Promise<unknown> => {
    try {
      await completeImagesUpload(iterationDatasetId);
      return undefined;
    } catch (requestError) {
      return requestError;
    }
  };

  const closeUploadOnServer = async () => {
    await sendUploadDone();
    await invalidateIterationDataset();
  };

  const failUpload = async (run: UploadRun, message: string) => {
    if (!isCurrentRun(run)) {
      return;
    }

    stopRun();
    setStage(ImageSetUploadStage.Failed, message);
    await closeUploadOnServer();
  };

  const finishUpload = async () => {
    // Every image is on the server, so pause and cancel no longer apply.
    stopRun();
    setStage(ImageSetUploadStage.Completing);

    const closeError = await sendUploadDone();

    if (isNil(closeError)) {
      setStage(ImageSetUploadStage.Uploaded);
    } else {
      setStage(
        ImageSetUploadStage.Failed,
        `${VALIDATION_ERROR_PREFIX}: ${getUploadErrorMessage(closeError)}`,
      );
    }

    if (!isDisposed) {
      await invalidateIterationDataset();
    }
  };

  const schedulePing = () => {
    const generation = pingGeneration;
    const isCurrentPing = () =>
      !isDisposed &&
      generation === pingGeneration &&
      stage === ImageSetUploadStage.NetworkError;

    pingTimer = setTimeout(async () => {
      if (!isCurrentPing()) {
        return;
      }

      const isBackendReachable = await pingBackend();

      if (!isCurrentPing()) {
        return;
      }

      if (isBackendReachable) {
        await syncWithServerAndRun(true);
        return;
      }

      schedulePing();
    }, NETWORK_PING_INTERVAL_MS);
  };

  const handleNetworkError = (run: UploadRun) => {
    if (!isCurrentRun(run)) {
      return;
    }

    stopRun();
    setStage(ImageSetUploadStage.NetworkError);
    schedulePing();
  };

  const armWatchdog = (run: UploadRun, timeoutMs: number) => {
    clearTimeout(watchdogTimer);
    watchdogTimer = setTimeout(() => handleNetworkError(run), timeoutMs);
  };

  /**
   * Runs one request of a run, retrying it as `getUploadErrorAction` decides.
   *
   * Network errors hand the upload to the network path. Permanent errors fail the upload.
   *
   * @param run - The run the request belongs to.
   * @param isSignedUrlRequest - Whether the request is the PUT to the storage signed URL.
   * @param request - The request to run.
   * @param onSignedUrlExpired - Called before a retry that needs a fresh signed URL.
   * @returns The request's value, or an unsuccessful outcome when the run stopped or failed.
   */
  const runWithRetries = async <T>(
    run: UploadRun,
    isSignedUrlRequest: boolean,
    request: () => Promise<T>,
    onSignedUrlExpired?: VoidFunction,
  ): Promise<RequestOutcome<T>> => {
    let retryCount = 0;

    while (isCurrentRun(run)) {
      try {
        const value = await request();
        return isCurrentRun(run)
          ? { isSuccessful: true, value }
          : failedOutcome;
      } catch (requestError) {
        if (!isCurrentRun(run)) {
          return failedOutcome;
        }

        const action = getUploadErrorAction(
          requestError,
          retryCount,
          isSignedUrlRequest,
        );

        if (action === UploadErrorAction.Ignore) {
          return failedOutcome;
        }

        if (action === UploadErrorAction.WaitForNetwork) {
          handleNetworkError(run);
          return failedOutcome;
        }

        if (action === UploadErrorAction.Fail) {
          await failUpload(run, getUploadErrorMessage(requestError));
          return failedOutcome;
        }

        if (action === UploadErrorAction.RefreshUrlAndRetry) {
          onSignedUrlExpired?.();
        }

        await wait(getRetryDelay(retryCount), run.controller.signal);
        retryCount += 1;
      }
    }

    return failedOutcome;
  };

  const fetchNextPresignedUrls = async (run: UploadRun) => {
    const batch = pendingFiles.splice(0, PRESIGNED_URL_BATCH_SIZE);

    try {
      const signedUrls = await fetchPresignedUrls(
        iterationDatasetId,
        batch.map(({ name }) => name),
        run.controller.signal,
      );

      if (signedUrls.length !== batch.length) {
        throw new MissingPresignedUrlsError();
      }

      if (isCurrentRun(run)) {
        readyUploads.push(
          ...batch.map((file, index) => ({
            file,
            signedUrl: signedUrls[index],
          })),
        );
      }
    } catch (requestError) {
      // Return the batch to the queue so a retry fetches its URLs again.
      if (isCurrentRun(run)) {
        pendingFiles.unshift(...batch);
      }
      throw requestError;
    }
  };

  // Workers share one presigned URL batch request instead of each starting their own.
  const acquireNextUpload = async (
    run: UploadRun,
  ): Promise<ReadyImageUpload | undefined> => {
    while (isCurrentRun(run)) {
      const nextUpload = readyUploads.shift();

      if (!isNil(nextUpload)) {
        return nextUpload;
      }

      if (isEmpty(pendingFiles) && isNil(presignedUrlsRequest)) {
        return undefined;
      }

      if (isNil(presignedUrlsRequest)) {
        const request = fetchNextPresignedUrls(run);
        const clearRequest = () => {
          if (presignedUrlsRequest === request) {
            presignedUrlsRequest = null;
          }
        };

        presignedUrlsRequest = request;
        request.then(clearRequest, clearRequest);
      }

      await presignedUrlsRequest;
    }

    return undefined;
  };

  const fetchSignedUrl = async (run: UploadRun, filename: string) => {
    const [signedUrl] = await fetchPresignedUrls(
      iterationDatasetId,
      [filename],
      run.controller.signal,
    );

    if (isNil(signedUrl)) {
      throw new MissingPresignedUrlsError();
    }

    return signedUrl;
  };

  const putImageWithProgress = async (
    run: UploadRun,
    { file, signedUrl }: ReadyImageUpload,
  ) => {
    activePutCount += 1;
    inFlightBytesByName.set(file.name, 0);
    armWatchdog(run, INITIAL_CONNECTION_TIMEOUT_MS);

    try {
      await putImage(signedUrl, file, {
        signal: run.controller.signal,
        onUploadProgress: (loadedBytes) => {
          if (!isCurrentRun(run)) {
            return;
          }

          inFlightBytesByName.set(file.name, loadedBytes);
          armWatchdog(run, PROGRESS_CONNECTION_TIMEOUT_MS);
        },
      });

      if (isCurrentRun(run)) {
        inFlightBytesByName.set(file.name, file.size);
      }
    } catch (requestError) {
      if (isCurrentRun(run)) {
        inFlightBytesByName.delete(file.name);
      }
      throw requestError;
    } finally {
      if (isCurrentRun(run)) {
        activePutCount -= 1;

        if (activePutCount === 0) {
          clearTimeout(watchdogTimer);
        }
      }
    }
  };

  const uploadImage = async (
    run: UploadRun,
    upload: ReadyImageUpload,
  ): Promise<boolean> => {
    const { file } = upload;
    let { signedUrl } = upload;
    let isSignedUrlExpired = false;

    const putOutcome = await runWithRetries(
      run,
      true,
      async () => {
        if (isSignedUrlExpired) {
          signedUrl = await fetchSignedUrl(run, file.name);
          isSignedUrlExpired = false;
        }

        await putImageWithProgress(run, { file, signedUrl });
      },
      () => {
        isSignedUrlExpired = true;
      },
    );

    if (!putOutcome.isSuccessful) {
      return false;
    }

    const markOutcome = await runWithRetries(run, false, () =>
      markImageUploaded(iterationDatasetId, file.name, run.controller.signal),
    );

    if (!markOutcome.isSuccessful) {
      return false;
    }

    inFlightBytesByName.delete(file.name);
    markImageCompleted(file.name);
    return true;
  };

  const runWorker = async (run: UploadRun) => {
    while (isCurrentRun(run)) {
      const acquireOutcome = await runWithRetries(run, false, () =>
        acquireNextUpload(run),
      );

      if (!acquireOutcome.isSuccessful || isNil(acquireOutcome.value)) {
        return;
      }

      const isUploaded = await uploadImage(run, acquireOutcome.value);

      if (!isUploaded) {
        return;
      }
    }
  };

  const runPool = async (run: UploadRun) => {
    if (stage !== ImageSetUploadStage.Uploading) {
      setStage(ImageSetUploadStage.Uploading);
    }

    const workerCount = Math.min(
      MAX_CONCURRENT_IMAGE_UPLOADS,
      pendingFiles.length,
    );
    await Promise.all(times(workerCount, () => runWorker(run)));

    if (!isCurrentRun(run)) {
      return;
    }

    // Every worker stopped while the run is still current, so none can finish the remaining images.
    if (completedImageNames.size < files.length) {
      await failUpload(run, IMAGE_UPLOAD_FAILED_MESSAGE);
      return;
    }

    await finishUpload();
  };

  /**
   * Starts a new run. It marks images the server already has as completed, optionally checks the
   * remaining files, then uploads them.
   *
   * @param shouldCheckFiles - Whether to check the remaining files for blacklisted formats again.
   */
  const syncWithServerAndRun = async (shouldCheckFiles: boolean) => {
    const run = beginRun();
    setStage(
      isEmpty(completedImageNames)
        ? ImageSetUploadStage.Starting
        : ImageSetUploadStage.Uploading,
    );

    const syncOutcome = await runWithRetries(run, false, () =>
      fetchUploadedImageNames(iterationDatasetId, run.controller.signal),
    );

    if (!syncOutcome.isSuccessful) {
      return;
    }

    diffAgainstServerImages(files, syncOutcome.value).onServer.forEach(
      ({ name }) => markImageCompleted(name),
    );
    pendingFiles = files.filter(({ name }) => !completedImageNames.has(name));

    if (shouldCheckFiles && !isEmpty(pendingFiles)) {
      const isAllowed = await ensureBatchFilesNotExecutable(pendingFiles);

      if (!isCurrentRun(run)) {
        return;
      }

      if (!isAllowed) {
        await failUpload(run, UploadFilesErrorMessages.BlacklistedFileFormat);
        return;
      }
    }

    await runPool(run);
  };

  const start = async () => {
    if (stage !== ImageSetUploadStage.Queued) {
      return;
    }

    await syncWithServerAndRun(false);
  };

  const pause = () => {
    if (!pausableImageSetUploadStages.includes(stage)) {
      return;
    }

    stopRun();
    setStage(ImageSetUploadStage.Paused);
  };

  const resume = async () => {
    if (stage !== ImageSetUploadStage.Paused) {
      return;
    }

    await syncWithServerAndRun(true);
  };

  const cancel = async () => {
    if (nonCancellableImageSetUploadStages.includes(stage)) {
      return;
    }

    stopRun();
    setStage(ImageSetUploadStage.Cancelled);
    await closeUploadOnServer();
  };

  const dispose = () => {
    stopRun();
    isDisposed = true;
  };

  return { start, pause, resume, cancel, dispose, getSnapshot };
};
